import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Grid,
  Modal,
  Stack,
  Text,
  TextInput,
  Textarea,
  createStyles,
} from "@mantine/core";
import { pdf } from "@react-pdf/renderer";
import { IconDownload, IconFileTypePdf } from "@tabler/icons-react";
import { saveAs } from "file-saver";
import moment from "moment";
import { PDFDocument } from "pdf-lib";
import { decrypt } from "../../utils";
import EMSFormPDF from "../EMSFormPDF";
import ClientSummaryPDF from "../ClientSummaryPDF/ClientSummaryPDF";

const useStyles = createStyles(() => ({
  modal: {
    "& .mantine-Modal-content": {
      background: "#ffffff",
      borderRadius: 20,
      maxHeight: "90vh",
      border: "1px solid #ece4d8",
    },
    "& .mantine-Modal-header": {
      background: "transparent",
      paddingBottom: 0,
      paddingTop: 18,
      paddingLeft: 22,
      paddingRight: 22,
    },
    "& .mantine-Modal-body": {
      padding: "8px 22px 22px",
      maxHeight: "calc(90vh - 64px)",
      overflowY: "auto",
    },
  },
  shell: {
    padding: "2px 0 8px",
    maxWidth: 1120,
    margin: "0 auto",
  },
  hero: {
    background: "#ffffff",
    border: "1px solid #e8dfd1",
    borderRadius: 18,
    padding: 24,
    marginBottom: 16,
  },
  heroTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 18,
    paddingBottom: 22,
    borderBottom: "1px solid #ddd2c2",
  },
  heroIdentity: {
    display: "flex",
    alignItems: "center",
    gap: 18,
  },
  avatar: {
    width: 88,
    height: 88,
    borderRadius: "50%",
    border: "4px solid #f6ca59",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    background: "#fff7df",
    color: "#8f6b00",
    fontSize: 22,
    fontWeight: 700,
    flexShrink: 0,
  },
  avatarImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  heroName: {
    color: "#202124",
    fontSize: 28,
    fontWeight: 700,
    lineHeight: 1.1,
    marginBottom: 10,
  },
  heroMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    color: "#6f6a62",
    fontSize: 15,
    alignItems: "center",
  },
  dot: {
    color: "#c0b5a5",
  },
  heroPills: {
    display: "none",
  },
  summaryRow: {
    display: "grid",
    gridTemplateColumns: "1.3fr 1fr 2.2fr 1fr 1fr",
    gap: 0,
    paddingTop: 18,
  },
  summaryCell: {
    paddingRight: 20,
    borderRight: "1px solid #ddd2c2",
  },
  summaryCellLast: {
    borderRight: "none",
    paddingRight: 0,
  },
  summaryLabel: {
    color: "#9f998f",
    fontSize: 13,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: 8,
  },
  summaryValue: {
    color: "#202124",
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.25,
  },
  allergyValue: {
    color: "#b53030",
  },
  card: {
    background: "#ffffff",
    border: "1px solid #e8dfd1",
    borderRadius: 18,
    overflow: "hidden",
    height: "100%",
  },
  cardHeader: {
    background: "#efebe3",
    color: "#202124",
    fontSize: 17,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.02em",
    padding: "16px 18px",
    borderBottom: "1px solid #e2dacd",
  },
  cardBody: {
    padding: 18,
  },
  infoRow: {
    display: "grid",
    gridTemplateColumns: "250px 1fr",
    gap: 16,
    padding: "12px 0",
    borderBottom: "1px solid #eee6d9",
  },
  infoRowLast: {
    borderBottom: "none",
  },
  infoLabel: {
    color: "#8a8680",
    fontSize: 15,
  },
  infoValue: {
    color: "#202124",
    fontSize: 18,
    fontWeight: 500,
    lineHeight: 1.35,
  },
  attachmentBox: {
    border: "1px solid #f0b437",
    background: "#fff6dc",
    borderRadius: 14,
    padding: "14px 16px",
    marginTop: 10,
  },
  attachmentOption: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "6px 0",
    cursor: "pointer",
  },
  attachmentOptionBox: {
    width: 28,
    height: 28,
    borderRadius: 8,
    border: "1px solid #f0b437",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    fontSize: 16,
    fontWeight: 700,
    flexShrink: 0,
  },
  attachmentOptionBoxActive: {
    background: "#f0b10a",
    borderColor: "#f0b10a",
  },
  attachmentOptionLabel: {
    color: "#202124",
    fontSize: 15,
    lineHeight: 1.45,
  },
  attachmentChecks: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    alignItems: "center",
  },
  smallMark: {
    width: 28,
    height: 28,
    borderRadius: 7,
    background: "#f0b10a",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
    fontWeight: 700,
  },
  attachmentText: {
    color: "#202124",
    fontSize: 15,
    lineHeight: 1.45,
  },
  medServiceRow: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  medLabel: {
    color: "#8a8680",
    fontSize: 15,
  },
  optionPill: {
    borderRadius: 999,
    padding: "8px 18px",
    fontSize: 14,
    fontWeight: 600,
    border: "1px solid #dfd5c4",
    color: "#8a8680",
    background: "#f8f4ec",
  },
  optionPillActive: {
    background: "#f0b10a",
    color: "#fff",
    borderColor: "#f0b10a",
  },
  checkboxGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  },
  checkboxTile: {
    border: "1px solid #e2dacd",
    borderRadius: 14,
    background: "#f7f3ec",
    padding: "14px 16px",
    minHeight: 64,
    display: "flex",
    alignItems: "center",
    gap: 14,
    cursor: "pointer",
  },
  checkboxTileActive: {
    borderColor: "#f0b10a",
    background: "#fff6dc",
  },
  checkboxIndicator: {
    width: 28,
    height: 28,
    borderRadius: 8,
    border: "1px solid #cfd5df",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    fontSize: 16,
    fontWeight: 700,
    flexShrink: 0,
  },
  checkboxIndicatorActive: {
    background: "#f0b10a",
    borderColor: "#f0b10a",
  },
  checkboxLabel: {
    fontSize: 14,
    color: "#2b2b2b",
  },
  sectionSubTitle: {
    color: "#8a8680",
    fontSize: 14,
    marginBottom: 10,
  },
  alertRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 14,
  },
  alertPill: {
    borderRadius: 999,
    padding: "8px 18px",
    border: "1px solid #dfd5c4",
    color: "#8a8680",
    background: "#f8f4ec",
    fontSize: 14,
    fontWeight: 600,
  },
  alertPillActive: {
    background: "#f0b10a",
    color: "#fff",
    borderColor: "#f0b10a",
  },
  emergencyGrid: {
    display: "grid",
    gridTemplateColumns: "1.4fr 1fr",
    gap: 16,
    marginTop: 18,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
    marginTop: 18,
    alignItems: "center",
    flexWrap: "wrap",
  },
  downloadButton: {
    height: 40,
    padding: "0 16px",
    borderRadius: 10,
    border: "1px solid #d7dce5",
    background: "#ffffff",
    color: "#26303c",
    fontWeight: 600,
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
  },
}));

const normalizePhone = (value) => value || "—";

const joinAddress = (...parts) => parts.filter(Boolean).join(", ");

const getInitials = (value = "") =>
  value
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "CL";

const keywordPresent = (source, keywords) => {
  const haystack = `${source || ""}`.toLowerCase();
  return keywords.some((keyword) => haystack.includes(keyword));
};

const safeDecrypt = (value) => {
  if (!value) return null;
  try {
    return decrypt(value) || null;
  } catch {
    return value;
  }
};

const KeyValueRows = ({ rows, classes }) =>
  rows.map((row, index) => (
    <Box
      key={row.label}
      className={classes.infoRow}
      style={index === rows.length - 1 ? { borderBottom: "none" } : undefined}
    >
      <Text className={classes.infoLabel}>{row.label}</Text>
      <Text className={classes.infoValue}>{row.value || "—"}</Text>
    </Box>
  ));

const conditionOptions = [
  { label: "Dentures", keywords: ["denture"] },
  { label: "Pacemaker", keywords: ["pacemaker"] },
  { label: "Eyeglasses", keywords: ["eyeglass", "glasses", "vision"] },
  { label: "Isolation precautions", keywords: ["isolation precaution", "isolation"] },
  { label: "Hearing aid (L/R)", keywords: ["hearing aid", "hearing"] },
  { label: "Cerebrovascular event", keywords: ["cerebrovascular", "stroke"] },
  { label: "Diabetes", keywords: ["diabetes", "diabetic"] },
  { label: "Frequent falls", keywords: ["fall", "frequent falls"] },
  { label: "Cardiovascular event", keywords: ["cardiovascular", "carditis", "cardiac"] },
];

const defaultChecklistState = (planData) => {
  const carePlanSource = planData?.data?.plan || planData?.servicePlanData || planData;
  const carePlanText = carePlanSource
    ? JSON.stringify(carePlanSource).toLowerCase()
    : "";

  return conditionOptions.reduce((accumulator, item) => {
    accumulator[item.label] = carePlanText
      ? keywordPresent(carePlanText, item.keywords)
      : false;
    return accumulator;
  }, {});
};

const EMSFormModal = ({
  opened,
  onClose,
  clientDetails,
  profile,
  planData,
  onDownloadComplete,
}) => {
  const { classes } = useStyles();
  const [emergencyNotes, setEmergencyNotes] = useState("");
  const [staffAndDate, setStaffAndDate] = useState("");
  const [hospitalDestination, setHospitalDestination] = useState("");
  const [didNotGoToHospital, setDidNotGoToHospital] = useState(false);
  const [authorizedRepNotified, setAuthorizedRepNotified] = useState(false);
  const [authorizedRepresentative, setAuthorizedRepresentative] = useState("");
  const [authorizedRepContactedBy, setAuthorizedRepContactedBy] = useState("");
  const [notificationDate, setNotificationDate] = useState(
    moment().format("MM/DD/YYYY")
  );
  const [attachmentChecks, setAttachmentChecks] = useState({
    advancedDirectives: true,
    faceSheet: true,
    hipaaRelease: true,
  });
  const [conditionSelections, setConditionSelections] = useState({});
  const [alertSelections, setAlertSelections] = useState({
    Person: true,
    Place: true,
    Time: false,
    Situation: false,
  });
  const [downloadLoading, setDownloadLoading] = useState(false);

  useEffect(() => {
    if (!opened) return;
    setEmergencyNotes("");
    setHospitalDestination("");
    setDidNotGoToHospital(false);
    setAuthorizedRepNotified(false);
    setAuthorizedRepresentative(
      clientDetails?.form2?.emergencyContactName || "—"
    );
    setAuthorizedRepContactedBy(profile?.user?.name || "");
    setStaffAndDate(
      `${profile?.user?.name || ""}${profile?.user?.name ? " - " : ""}${moment().format("MM/DD/YYYY")}`
    );
    setNotificationDate(moment().format("MM/DD/YYYY"));
    setAttachmentChecks({
      advancedDirectives: true,
      faceSheet: true,
      hipaaRelease: true,
    });
    setConditionSelections(defaultChecklistState(planData));
    setAlertSelections({
      Person: true,
      Place: true,
      Time: false,
      Situation: false,
    });
  }, [
    opened,
    profile?.user?.name,
    clientDetails?.form2?.emergencyContactName,
    planData,
  ]);

  if (!clientDetails) return null;

  const companyName =
    profile?.business?.companyName ||
    clientDetails?.business?.companyName ||
    clientDetails?.companyName ||
    profile?.facility?.name ||
    "Business";

  const facilityName = profile?.facility?.name || companyName;
  const facilityAddress =
    joinAddress(
      profile?.facility?.address,
      profile?.facility?.city,
      profile?.facility?.state,
      profile?.facility?.zipCode
    ) ||
    joinAddress(
      clientDetails?.facility?.address,
      clientDetails?.facility?.city,
      clientDetails?.facility?.state,
      clientDetails?.facility?.zipCode
    ) ||
    clientDetails?.form1?.previousAddress ||
    "—";

  const facilityRows = [
    { label: "Facility transferred from", value: facilityName },
    {
      label: "Facility phone",
      value:
        profile?.facility?.phoneNumber ||
        profile?.business?.phoneNumber ||
        "—",
    },
    { label: "Address", value: facilityAddress },
    {
      label: "Contact name",
      value:
        clientDetails?.form2?.caseManagerName ||
        profile?.user?.name ||
        "—",
    },
    {
      label: "Cell phone",
      value: normalizePhone(clientDetails?.form2?.caseManagerPhoneNumber),
    },
    {
      label: "Email",
      value: clientDetails?.form2?.caseManagerEmail || profile?.user?.email || "—",
    },
  ];

  const medicationRows = [
    {
      label: "Current pharmacy",
      value: clientDetails?.form2?.pharmacyName || "—",
    },
    {
      label: "Pharmacy phone",
      value: normalizePhone(clientDetails?.form2?.pharmacyPhoneNumber),
    },
    {
      label: "Pharmacy address",
      value:
        clientDetails?.form2?.pharmacyAddress ||
        clientDetails?.form2?.pharmacyEmail ||
        "—",
    },
  ];

  const contactRows = [
    {
      label: "Primary care physician",
      value: clientDetails?.form2?.pcpName || "—",
    },
    {
      label: "Physician phone",
      value:
        clientDetails?.form2?.pcpPhoneNumber ||
        clientDetails?.form2?.pcpEmail ||
        "—",
    },
    {
      label: "Authorized rep / POA",
      value: clientDetails?.form2?.emergencyContactName || "—",
    },
    {
      label: "Rep phone",
      value: normalizePhone(clientDetails?.form2?.emergencyContactPhoneNumber),
    },
  ];

  const clientImage = safeDecrypt(clientDetails?.imageurl);
  const facilityPhoto = safeDecrypt(
    clientDetails?.facility?.imageurl || profile?.facility?.imageurl
  );
  const emsFormData = {
    emergencyNotes,
    staffAndDate,
    hospitalDestination,
    didNotGoToHospital,
    authorizedRepNotified,
    authorizedRepresentative,
    authorizedRepContactedBy,
    notificationDate,
    attachmentChecks,
    conditionSelections,
    alertSelections,
  };

  const handleDownloadPackage = async () => {
    if (!clientDetails) return;
    setDownloadLoading(true);
    try {
      const clientName = clientDetails?.name || "client";
      const dateStamp = moment().format("YYYY-MM-DD");
      const emsBlob = await pdf(
        <EMSFormPDF
          clientData={clientDetails}
          profile={profile}
          formData={emsFormData}
        />
      ).toBlob();

      const includeFaceSheet = attachmentChecks.faceSheet;

      const packet = await PDFDocument.create();

      const appendBlobPages = async (blob) => {
        const bytes = await blob.arrayBuffer();
        const sourcePdf = await PDFDocument.load(bytes);
        const pageIndices = sourcePdf.getPageIndices();
        const copiedPages = await packet.copyPages(sourcePdf, pageIndices);
        copiedPages.forEach((page) => packet.addPage(page));
      };

      await appendBlobPages(emsBlob);

      if (includeFaceSheet) {
        const faceSheetBlob = await pdf(
          <ClientSummaryPDF
            clientData={clientDetails}
            facilityName={profile?.business?.companyName || clientDetails?.business?.companyName || profile?.facility?.name}
            facilityPhoto={facilityPhoto}
          />
        ).toBlob();
        await appendBlobPages(faceSheetBlob);
      }

      const mergedBytes = await packet.save();
      const mergedBlob = new Blob([mergedBytes], { type: "application/pdf" });
      saveAs(
        mergedBlob,
        `${clientName.replace(/\s+/g, "_")}_EMS_Packet_${dateStamp}.pdf`
      );
      onDownloadComplete?.(emsFormData);
    } catch (error) {
      console.error("EMS package generation error:", error);
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="EMS Form"
      size="72rem"
      centered
      overlayProps={{ blur: 3 }}
      className={classes.modal}
      zIndex={1000}

    >
      <Box className={classes.shell}>
        <Box className={classes.hero}>
          <Box className={classes.heroTop}>
            <Box className={classes.heroIdentity}>
              <Box className={classes.avatar}>
                {clientImage ? (
                  <img
                    src={clientImage}
                    alt="client"
                    className={classes.avatarImage}
                  />
                ) : (
                  getInitials(clientDetails?.name)
                )}
              </Box>
              <Box>
                <Text className={classes.heroName}>{clientDetails?.name}</Text>
                <Box className={classes.heroMeta}>
                  <span>{clientDetails?.form1?.gender || "—"}</span>
                  <span className={classes.dot}>·</span>
                  <span>{`DOB: ${moment(clientDetails?.form1?.dob).format("MM/DD/YYYY")}`}</span>
                  <span className={classes.dot}>·</span>
                  <span>{`Admitted: ${moment(clientDetails?.form1?.admissionDate).format("MM/DD/YYYY")}`}</span>
                  <span className={classes.dot}>·</span>
                  <span>{facilityName}</span>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box className={classes.summaryRow}>
            {[
              { label: "Resident", value: clientDetails?.name },
              {
                label: "Date of birth",
                value: moment(clientDetails?.form1?.dob).format("MM/DD/YYYY"),
              },
              { label: "Facility transferred from", value: facilityName },
              { label: "Physician", value: clientDetails?.form2?.pcpName || "—" },
              {
                label: "Allergies",
                value:
                  (clientDetails?.form1?.knownAllergies || []).filter(Boolean).join(", ") ||
                  "None",
                danger: true,
              },
            ].map((item, index) => (
              <Box
                key={item.label}
                className={classes.summaryCell}
                style={index === 4 ? { borderRight: "none", paddingRight: 0 } : undefined}
              >
                <Text className={classes.summaryLabel}>{item.label}</Text>
                <Text
                  className={classes.summaryValue}
                  style={item.danger ? { color: "#b53030" } : undefined}
                >
                  {item.value}
                </Text>
              </Box>
            ))}
          </Box>
        </Box>

        <Grid gutter={24}>
          <Grid.Col span={6}>
            <Box className={classes.card}>
              <Box className={classes.cardHeader}>Facility Information</Box>
              <Box className={classes.cardBody}>
                <KeyValueRows rows={facilityRows} classes={classes} />
              </Box>
            </Box>
          </Grid.Col>

          <Grid.Col span={6}>
            <Box className={classes.card}>
              <Box className={classes.cardHeader}>Medication Information</Box>
              <Box className={classes.cardBody}>
                <Box mt={16}>
                  <KeyValueRows rows={medicationRows} classes={classes} />
                </Box>
              </Box>
            </Box>
          </Grid.Col>

          <Grid.Col span={6}>
            <Box className={classes.card}>
              <Box className={classes.cardHeader}>Medical Contacts, DNR & HIPAA</Box>
              <Box className={classes.cardBody}>
                <KeyValueRows rows={contactRows} classes={classes} />

                <Box className={classes.attachmentBox}>
                  <Stack spacing={8} style={{ width: "100%" }}>
                    {[
                      {
                        key: "advancedDirectives",
                        label: "Advanced Directives / DNR (orange form) / POLST attached",
                      },
                      { key: "faceSheet", label: "Face Sheet attached" },
                      { key: "hipaaRelease", label: "HIPAA Release attached" },
                    ].map((item) => (
                      <Box
                        key={item.key}
                        className={classes.attachmentOption}
                        onClick={() =>
                          setAttachmentChecks((current) => ({
                            ...current,
                            [item.key]: !current[item.key],
                          }))
                        }
                      >
                        <Box
                          className={classes.attachmentOptionBox}
                          style={
                            attachmentChecks[item.key]
                              ? { background: "#f0b10a", borderColor: "#f0b10a" }
                              : undefined
                          }
                        >
                          {attachmentChecks[item.key] ? "✓" : ""}
                        </Box>
                        <Text className={classes.attachmentOptionLabel}>
                          {item.label}
                        </Text>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Box>
            </Box>
          </Grid.Col>

          <Grid.Col span={6}>
            <Box className={classes.card}>
              <Box className={classes.cardHeader}>Physical & Mental Conditions</Box>
              <Box className={classes.cardBody}>
                <Box className={classes.checkboxGrid}>
                  {conditionOptions.map((item) => (
                    <Box
                      key={item.label}
                      className={classes.checkboxTile}
                      style={
                        conditionSelections[item.label]
                          ? { borderColor: "#f0b10a", background: "#fff6dc" }
                          : undefined
                      }
                      onClick={() =>
                        setConditionSelections((current) => ({
                          ...current,
                          [item.label]: !current[item.label],
                        }))
                      }
                    >
                      <Box
                        className={classes.checkboxIndicator}
                        style={
                          conditionSelections[item.label]
                            ? { background: "#f0b10a", borderColor: "#f0b10a" }
                            : undefined
                        }
                      >
                        {conditionSelections[item.label] ? "✓" : ""}
                      </Box>
                      <Text className={classes.checkboxLabel}>{item.label}</Text>
                    </Box>
                  ))}
                </Box>

                <Text className={classes.sectionSubTitle} mt={18}>
                  Alert and oriented to
                </Text>
                <Box className={classes.alertRow}>
                  {Object.keys(alertSelections).map((label) => (
                    <Box
                      key={label}
                      className={classes.alertPill}
                      style={
                        alertSelections[label]
                          ? { background: "#f0b10a", color: "#fff", borderColor: "#f0b10a" }
                          : undefined
                      }
                      onClick={() =>
                        setAlertSelections((current) => ({
                          ...current,
                          [label]: !current[label],
                        }))
                      }
                    >
                      {label}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Grid.Col>

          <Grid.Col span={12}>
            <Box className={classes.card}>
              <Box className={classes.cardHeader}>Complete at Time of Emergency</Box>
              <Box className={classes.cardBody}>
                <Textarea
                  label="Reason(s) emergency response requested"
                  placeholder="Enter the reason for EMS response"
                  minRows={4}
                  value={emergencyNotes}
                  onChange={(event) => setEmergencyNotes(event.currentTarget.value)}
                />

                <Box className={classes.emergencyGrid}>
                  <TextInput
                    label="Name of staff and date"
                    value={staffAndDate}
                    onChange={(event) => setStaffAndDate(event.currentTarget.value)}
                  />
                  <Stack spacing={8}>
                    <Checkbox
                      checked={didNotGoToHospital}
                      onChange={(event) =>
                        setDidNotGoToHospital(event.currentTarget.checked)
                      }
                      label="Resident did not go to the hospital"
                      color="yellow"
                    />
                    <TextInput
                      label="Hospital / destination"
                      placeholder="Hospital name and address"
                      value={hospitalDestination}
                      disabled={didNotGoToHospital}
                      onChange={(event) =>
                        setHospitalDestination(event.currentTarget.value)
                      }
                    />
                  </Stack>
                </Box>

                <Stack mt={18} spacing={12}>
                  <Checkbox
                    checked={authorizedRepNotified}
                    onChange={(event) =>
                      setAuthorizedRepNotified(event.currentTarget.checked)
                    }
                    label="Advised authorized representative of the hospital resident will be transferred to."
                    color="yellow"
                  />
                  <Flex gap={16}>
                    <TextInput
                      label="Authorized representative / POA"
                      value={authorizedRepresentative}
                      onChange={(event) =>
                        setAuthorizedRepresentative(event.currentTarget.value)
                      }
                      style={{ flex: 1 }}
                    />
                    <TextInput
                      label="Contacted by"
                      value={authorizedRepContactedBy}
                      onChange={(event) =>
                        setAuthorizedRepContactedBy(event.currentTarget.value)
                      }
                      style={{ flex: 1 }}
                    />
                    <TextInput
                      label="Date"
                      value={notificationDate}
                      onChange={(event) =>
                        setNotificationDate(event.currentTarget.value)
                      }
                      style={{ width: 220 }}
                    />
                  </Flex>
                </Stack>

                <Box className={classes.actions}>
                  <Button
                    className={classes.downloadButton}
                    variant="default"
                    onClick={handleDownloadPackage}
                    loading={downloadLoading}
                  >
                    <IconFileTypePdf size={18} stroke={1.8} />
                    {downloadLoading ? "Preparing Download..." : "Download EMS Form"}
                    {!downloadLoading && <IconDownload size={16} stroke={1.8} />}
                  </Button>
                  <Button variant="default" onClick={onClose}>
                    Close
                  </Button>
                </Box>
              </Box>
            </Box>
          </Grid.Col>
        </Grid>
      </Box>
    </Modal>
  );
};

export default EMSFormModal;
